import { useCallback } from 'react';
import { useNavigate, useParams } from '@/util/react-router-dom';
import { useVisitNavigation } from '@/contexts/visit-navigation-context';

type RouteTarget = {
  path: string;
  state?: unknown;
  replace?: boolean;
};

type ResolveArgs = {
  isIncomplete: boolean;
  editMode: string | null;
  defaults: {
    review: string;
    dashboard: string;
    next: string;
  };
};

type RouteResolver = (args: ResolveArgs) => RouteTarget;

export type PostSubmitPolicy = {
  resolveEdit?: RouteResolver;
  resolveIncomplete?: RouteResolver;
  resolveComplete?: RouteResolver;
};

function resolvePostSubmitTarget(args: ResolveArgs, policy?: PostSubmitPolicy): RouteTarget {
  const defaultEdit: RouteResolver = ({ defaults, editMode }) => ({
    path: defaults.review,
    state: { editMode },
  });

  const defaultIncomplete: RouteResolver = ({ defaults }) => ({
    path: defaults.dashboard,
  });

  const defaultComplete: RouteResolver = ({ defaults, editMode }) => ({
    path: defaults.next,
    state: { editMode },
  });

  if (args.editMode) {
    return (policy?.resolveEdit ?? defaultEdit)(args);
  }

  if (args.isIncomplete) {
    return (policy?.resolveIncomplete ?? defaultIncomplete)(args);
  }

  return (policy?.resolveComplete ?? defaultComplete)(args);
}

type NavigatePostSubmitArgs = {
  isIncomplete?: boolean;
  policy?: PostSubmitPolicy;
};

export function usePostSubmitNavigation() {
  const navigate = useNavigate();
  const { visitId } = useParams();
  const { nextStep, editMode } = useVisitNavigation();

  const navigatePostSubmit = useCallback(
    ({ isIncomplete = false, policy }: NavigatePostSubmitArgs = {}) => {
      const encounterId = Number(visitId);
      const target = resolvePostSubmitTarget(
        {
          isIncomplete,
          editMode,
          defaults: {
            review: `/visit/${encounterId}/review`,
            dashboard: '/',
            next: nextStep?.path ?? '/',
          },
        },
        policy,
      );

      navigate(target.path, {
        state: target.state,
        replace: target.replace,
      });
    },
    [editMode, navigate, nextStep?.path, visitId],
  );

  return { navigatePostSubmit };
}
